import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import getConfig from '@/utils.js'

// Eager-loaded components
import Dashboard from '@/components/Dashboard.vue'
import PleaseUpgrade from '@/components/PleaseUpgrade.vue'
import CampaignDashboard from '@/components/campaign/Dashboard.vue'
import CampaignList from '@/components/campaign/List.vue'
import CampaignCreate from '@/components/campaign/Create.vue'
import Sweepstake from '@/components/campaign/Sweepstake.vue'
import Coupon from '@/components/campaign/Coupon.vue'
import Tip from '@/components/campaign/Tip.vue'
import ViewInApp from '@/components/campaign/ViewInApp.vue'
import AccessCodeSetList from '@/components/access-code-set/List.vue'
import AccessCodeSetEdit from '@/components/access-code-set/Edit.vue'
import Todo from '@/views/Todo.vue'

const routes = [
  {
    path: '/',
    name: 'dashboard',
    component: Dashboard
  },
  {
    path: '/please-upgrade',
    name: 'please-upgrade',
    component: PleaseUpgrade
  },
  {
    path: '/create',
    name: 'merchant-create',
    component: () => import('@/components/merchant/Create.vue')
  },
  {
    path: '/:merchantIdent/merchant',
    name: 'merchant-edit',
    component: () => import('@/components/merchant/Merchant.vue'),
    props: true
  },
  {
    path: '/:merchantIdent/accounts',
    name: 'merchant-accounts',
    component: () => import('@/components/merchant/Accounts.vue'),
    props: true
  },
  {
    path: '/:merchantIdent/locations',
    name: 'merchant-locations',
    component: () => import('@/components/merchant/Locations.vue'),
    props: true
  },
  {
    path: '/:merchantIdent/notifications',
    name: 'merchant-notify',
    component: () => import('@/components/Notifications.vue'),
    props: true
  },
  {
    path: '/access-code-sets',
    name: 'access-code-set',
    component: AccessCodeSetList,
    beforeEnter: (to, from) => {
      const authStore = useAuthStore()
      if (authStore.isAdmin) {
        return true
      }
      return { path: '/please-upgrade' }
    },
    props: true,
    children: [
      {
        path: ':id',
        name: 'access-code-set-edit',
        component: AccessCodeSetEdit,
        props: true
      },
      {
        path: ':ident/code-usage',
        name: 'access-code-set-usage',
        component: Todo,
        props: true
      }
    ]
  },
  {
    path: '/:merchantIdent/campaigns',
    name: 'campaigns-dashboard',
    component: CampaignDashboard,
    props: true,
    children: [
      {
        path: 'list',
        name: 'campaigns-list',
        component: CampaignList,
        props: (route) => ({ query: route.query })
      },
      {
        path: 'create',
        name: 'campaigns-create',
        component: CampaignCreate
      },
      {
        path: 'sweepstake/:campaignIdent',
        component: Sweepstake,
        props: true,
        children: [
          {
            path: 'benefit',
            name: 'sweepstake-benefit',
            component: () => import('@/components/campaign/sweepstake/PrizeSet.vue'),
            props: true
          },
          {
            path: 'content',
            name: 'sweepstake-content',
            component: () => import('@/components/campaign/generic/Content.vue'),
            props: true
          },
          {
            path: 'image',
            name: 'sweepstake-image',
            component: () => import('@/components/campaign/generic/Image.vue'),
            props: true
          },
          {
            path: 'image/:imageIdent',
            name: 'sweepstake-image-detail',
            component: () => import('@/components/campaign/generic/ImageDetail.vue'),
            props: true
          },
          {
            path: 'handling',
            name: 'sweepstake-handling',
            component: () => import('@/components/campaign/sweepstake/Handling.vue'),
            props: true
          },
          {
            path: 'requireddata',
            name: 'sweepstake-requireddata',
            component: () => import('@/components/campaign/generic/RequiredData.vue'),
            props: true
          },
          {
            path: 'review',
            name: 'sweepstake-review',
            component: () => import('@/components/campaign/generic/Review.vue'),
            props: true
          }
        ]
      },
      {
        path: 'coupon/:campaignIdent',
        component: Coupon,
        props: true,
        children: [
          {
            path: 'benefit',
            name: 'coupon-benefit',
            component: () => import('@/components/campaign/coupon/Benefit.vue'),
            props: true
          },
          {
            path: 'content',
            name: 'coupon-content',
            component: () => import('@/components/campaign/generic/Content.vue'),
            props: true
          },
          {
            path: 'image',
            name: 'coupon-image',
            component: () => import('@/components/campaign/generic/Image.vue'),
            props: true
          },
          {
            path: 'image/:imageIdent',
            name: 'coupon-image-detail',
            component: () => import('@/components/campaign/generic/ImageDetail.vue'),
            props: true
          },
          {
            path: 'handling',
            name: 'coupon-handling',
            component: () => import('@/components/campaign/coupon/Handling.vue'),
            props: true
          },
          {
            path: 'requireddata',
            name: 'coupon-requireddata',
            component: () => import('@/components/campaign/generic/RequiredData.vue'),
            props: true
          },
          {
            path: 'review',
            name: 'coupon-review',
            component: () => import('@/components/campaign/generic/Review.vue'),
            props: true
          }
        ]
      },
      {
        path: 'tip/:campaignIdent',
        component: Tip,
        props: true,
        beforeEnter: (to, from) => {
          const authStore = useAuthStore()
          if (authStore.isAdmin || authStore.isAgency) {
            return true
          }
          return { path: '/please-upgrade' }
        },
        children: [
          {
            path: '',
            name: 'tip-benefit',
            component: () => import('@/components/campaign/tip/Benefit.vue'),
            props: true
          },
          {
            path: 'content',
            name: 'tip-content',
            component: () => import('@/components/campaign/tip/Content.vue'),
            props: true
          },
          {
            path: 'image',
            name: 'tip-image',
            component: () => import('@/components/campaign/generic/Image.vue'),
            props: true
          },
          {
            path: 'image/:imageIdent',
            name: 'tip-image-detail',
            component: () => import('@/components/campaign/generic/ImageDetail.vue'),
            props: true
          },
          {
            path: 'handling',
            name: 'tip-handling',
            component: () => import('@/components/campaign/tip/Handling.vue'),
            props: true
          },
          {
            path: 'requireddata',
            name: 'tip-requireddata',
            component: () => import('@/components/campaign/generic/RequiredData.vue'),
            props: true
          },
          {
            path: 'review',
            name: 'tip-review',
            component: () => import('@/components/campaign/generic/Review.vue'),
            props: true
          }
        ]
      },
      {
        path: 'view-in-app/:campaignIdent',
        name: 'view-campaign-in-app',
        component: ViewInApp,
        props: true
      }
    ]
  },
  {
    path: '/oauth2/logout',
    name: 'oauth2-logout',
    component: () => import('@/components/oauth2/Logout.vue')
  },
  {
    path: '/oauth2/postback',
    name: 'oauth2-postback',
    component: () => import('@/components/oauth2/Postback.vue')
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes
})

// Build login URL
const loginUrl =
  getConfig().oauthApiUrl +
  '/o/oauth2/auth?response_type=token&scope=base&client_id=' +
  getConfig().oauthClientId +
  '&redirect_uri=' +
  getConfig().oauthPostback +
  '&title=' +
  getConfig().loginHint

// Global navigation guard
router.beforeEach((to, from) => {
  const publicPages = ['/oauth2/logout', '/oauth2/postback']
  const authRequired = !publicPages.includes(to.path)

  const authStore = useAuthStore()

  if (authRequired && !authStore.validJWT) {
    localStorage.setItem('continueAfterLogin', to.fullPath)
    authStore.removeToken()
    window.location.href = loginUrl
    return false
  }

  if (
    to.path.match('/merchant/create/edit') &&
    authStore.merchantLevel === 0 &&
    authStore.merchantSearchType === 'single'
  ) {
    return { path: '/please-upgrade' }
  }

  return true
})

export default router
